import { ethers } from "ethers-v6";
import tokenService from "../../common/tokenService";

// Default configuration for ethers implementation
export const defaultConfig = {
  apiUrl: "https://prodapi.symphony.ag",
  timeout: 10000,
  chainId: 1329, // SEI
  chainName: "sei",
  rpcUrl: "https://evm-rpc.sei-apis.com",
  nativeAddress: "0x0",
  wrappedNativeAddress: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7",
  slippage: "1",
  provider: undefined,
  functionName: "executeSwaps",
  additionalTokens: {},
  overrideDefaultTokens: false, // When true, only use additionalTokens instead of merging with default list
  multicall: "0xcA11bde05977b3631167028862bE2a173976CA11",
  feeParams: {
    paramFee: "0",
    feeAddress: "0x0000000000000000000000000000000000000000",
    feeSharePercentage: "0",
  },
};

// Create the initial provider
const initialProvider = new ethers.JsonRpcProvider(defaultConfig.rpcUrl, {
  chainId: defaultConfig.chainId,
  name: defaultConfig.chainName,
});

// Initialize the current configuration with the provider
let currentConfig = { ...defaultConfig, provider: initialProvider };

export const getConfig = () => {
  // Use tokenService to get static token list with merging logic applied
  const tokens = tokenService.getStaticTokenList(
    currentConfig.chainId,
    currentConfig.additionalTokens,
    currentConfig.overrideDefaultTokens,
    currentConfig.nativeAddress
  );

  return {
    ...currentConfig,
    tokens,
  };
};

export const setConfig = (newConfig = {}) => {
  if (newConfig.functionName && newConfig.functionName !== "executeSwaps") {
    throw new Error('Invalid functionName. Must be "executeSwaps"');
  }

  // Validate and normalize additionalTokens if provided using tokenService
  if (newConfig.additionalTokens) {
    newConfig.additionalTokens = tokenService._validateAdditionalTokens(
      newConfig.additionalTokens
    );
  }

  if (newConfig?.feeParams) {
    // First, check if all required fields are present
    if (
      newConfig.feeParams.feeAddress === undefined ||
      newConfig.feeParams.paramFee === undefined ||
      newConfig.feeParams.feeSharePercentage === undefined
    )
      throw new Error(
        "Missing split fee parameters! paramFee, feeAddress, feeShareePercentage parameters are required for enabling custom fee split behavior."
      );

    //check fee address is valid
    if (!/^0x[a-fA-F0-9]{40}$/.test(newConfig.feeParams.feeAddress))
      throw new Error("feeAddress must be a valid ethereum address!");

    //check fee share perc. is valid (use isNaN to allow zero values)
    if (isNaN(Number(newConfig.feeParams.feeSharePercentage)))
      throw new Error(
        "feeSharePercentage must be a number or string representing a number! "
      );
    //check fee share perc. is between 0 - 100%
    if (
      Number(newConfig.feeParams.feeSharePercentage) > 10000 ||
      Number(newConfig.feeParams.feeSharePercentage) < 0
    )
      throw new Error(
        "feeSharePercentage must represent a percentage in bps. Should be between 0 and 10000. (10000 = 100%)"
      );

    //check fee param perc. is valid (use isNaN to allow zero values)
    if (isNaN(Number(newConfig.feeParams.paramFee)))
      throw new Error(
        "paramFee must be a number or string representing a number! "
      );
    //check paramFee perc. is between 0 - 100%
    if (
      Number(newConfig.feeParams.paramFee) > 10000 ||
      Number(newConfig.feeParams.paramFee) < 0
    )
      throw new Error(
        "paramFee must represent a percentage in bps. Should be between 0 and 10000. (10000 = 100%)"
      );
  }

  // Handle native address changes using tokenService
  // if (
  //   newConfig?.nativeAddress &&
  //   newConfig?.nativeAddress !== currentConfig.nativeAddress
  // ) {
  //   const newChainId = newConfig?.chainId || currentConfig.chainId;
  //   tokenService.updateNativeAddress(
  //     newChainId,
  //     currentConfig.nativeAddress,
  //     newConfig.nativeAddress
  //   );
  // }

  // Check if rpcUrl has changed and recreate provider if needed
  const shouldRecreateProvider =
    newConfig.rpcUrl && newConfig.rpcUrl !== currentConfig.rpcUrl;

  // Update the current config
  currentConfig = {
    ...currentConfig,
    ...newConfig,
  };

  // Recreate provider if rpcUrl has changed
  if (shouldRecreateProvider) {
    const updatedProvider = new ethers.JsonRpcProvider(currentConfig.rpcUrl, {
      chainId: currentConfig.chainId,
      name: currentConfig.chainName,
    });
    currentConfig.provider = updatedProvider;
  }
};

export const resetConfig = () => {
  // Create a new provider with the default rpcUrl
  const defaultProvider = new ethers.JsonRpcProvider(defaultConfig.rpcUrl, {
    chainId: defaultConfig.chainId,
    name: defaultConfig.chainName,
  });

  // Reset the config with the new provider
  currentConfig = { ...defaultConfig, provider: defaultProvider };
};
